import { Application } from '../types';

/**
 * A fully-resolved document request, independent of which URL scheme the browser is on.
 *
 * Two schemes coexist permanently (see URL_RESTRUCTURE_PLAN.md, Decision 4 / Blocker 3):
 *
 *   NEW (production)  /{slug}/{resourcePath}
 *       Always implicitly `version === 'main'`. Cannot express a revision.
 *
 *   OLD (preview)     /application/{appId}/{slug}/{version}/{resourcePath}
 *       Carries a git commit hash in `version`, which is the ONLY way a non-`main`
 *       revision reaches the backend. CloudHub's "Open Preview" button depends on it,
 *       so this route is permanent — it is not a migration leftover.
 *
 * Everything downstream of `resolveDocumentTarget` consumes this shape and never
 * inspects the URL again. Keeping the branch in exactly one function is deliberate: the
 * documented failure mode of this codebase is two near-identical code paths drifting
 * apart, so scheme-specific `if` statements must not be scattered through components.
 */
export interface DocumentTarget {
  /** CloudHub user-manual id, needed by the (unchanged) proxy API. */
  appId: string;
  /** Slugified service name. */
  appNameSlug: string;
  /** `'main'` for published content, or a git commit hash for preview. */
  version: string;
  /** Content path inside the document package; never empty. */
  resourcePath: string;
  /**
   * Prefix for stable, shareable URLs of this document — the frontend counterpart of the
   * backend's `baseNavPath`. Binary resource URLs are built from it, so the address bar
   * keeps whichever scheme the user actually arrived on.
   */
  baseNavPath: string;
}

/** Raw route params, from either of the two routes registered in App.tsx. */
export interface DocumentRouteParams {
  /** Old scheme only: the appId. Named `uuid` by the route definition. */
  uuid?: string;
  /** Old scheme only: the slug. Named `name` by the route definition. */
  name?: string;
  /** Old scheme only: `'main'` or a commit hash. */
  version?: string;
  /** New scheme only: the slug. */
  appNameSlug?: string;
  /** Splat, both schemes. */
  resourcePath?: string;
}

/** Old-scheme nav prefix: `/application/{appId}/{slug}/{version}`. */
function buildLegacyNavPath(appId: string, slug: string, version: string): string {
  return `/application/${appId}/${slug}/${version}`;
}

/**
 * Whether the backend confirmed that `/{app.slug}/...` resolves back to `app`.
 *
 * `/api/v1/apps` emits a bare-slug `entryPath` only for such apps and an old-scheme
 * `/application/...` entryPath otherwise (manuals missing from the anonymous list,
 * slug-collision losers) -- see `getCanonicalSlug` in
 * backend/src/services/cloudhub-docs-service.ts.
 */
function hasCanonicalSlug(app: Application): boolean {
  return !(app.entryPath || '').startsWith('/application/');
}

/**
 * Picks the application owning `slug`, mirroring the backend's bare-slug route.
 *
 * The backend resolves bare slugs through the ANONYMOUS list only, while this list may
 * be the caller's authenticated one, so the backend's verdict (encoded in `entryPath`,
 * see {@link hasCanonicalSlug}) wins first. Among the remaining candidates the match is
 * case-insensitive and the LOWEST `id` wins (`rebuildSlugIndex` in
 * backend/src/services/cloudhub-docs-service.ts).
 *
 * The two must agree. If the frontend resolved a different appId than the backend used
 * when rewriting the page's links, the document would load but its navigation would
 * point into a different manual — a bug that only appears for colliding slugs and would
 * be very hard to trace back to here.
 */
export function findApplicationBySlug(apps: Application[], slug: string): Application | null {
  const normalized = slug.toLowerCase();
  const candidates = apps.filter((app) => (app.slug || '').toLowerCase() === normalized);
  if (candidates.length === 0) {
    return null;
  }
  if (candidates.length === 1) {
    return candidates[0];
  }
  return [...candidates].sort(
    (a, b) =>
      Number(hasCanonicalSlug(b)) - Number(hasCanonicalSlug(a)) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  )[0];
}

/**
 * Resolves route params from either scheme into a single {@link DocumentTarget}.
 *
 * `loadApplications` is only invoked for the new scheme, so preview keeps working even
 * if the application list is unavailable, and the old scheme costs no extra request.
 *
 * @returns the resolved target, or `null` when the URL is unusable (no slug, or no
 *          application owns the slug). `null` is a 404, not an error — callers render
 *          "Document Not Found". If `loadApplications` itself rejects, the rejection is
 *          propagated so real upstream failures (401/429/502) keep their own handling
 *          instead of being flattened into a misleading 404.
 */
export async function resolveDocumentTarget(
  params: DocumentRouteParams,
  loadApplications: () => Promise<Application[]>
): Promise<DocumentTarget | null> {
  const resourcePath = params.resourcePath || 'index.html';

  // Old scheme: every component is already in the URL. Used verbatim — this is the
  // preview path and must behave exactly as it did before the bare-slug scheme existed.
  if (params.uuid && params.name && params.version) {
    return {
      appId: params.uuid,
      appNameSlug: params.name,
      version: params.version,
      resourcePath,
      baseNavPath: buildLegacyNavPath(params.uuid, params.name, params.version),
    };
  }

  if (!params.appNameSlug) {
    return null;
  }

  // New scheme: the URL carries no appId, so resolve it from the application list.
  // NOTE: the list is an IDENTITY source only. It includes applications the caller
  // cannot read (the backend returns them with `requiresAuth: true`), which is what
  // makes deep-link sign-in work: the slug resolves, the content request 401s, and the
  // OAuth flow starts. Resolving a slug must never be treated as proof of access.
  const application = findApplicationBySlug(await loadApplications(), params.appNameSlug);
  if (!application) {
    return null;
  }

  return {
    appId: application.id,
    appNameSlug: params.appNameSlug,
    version: 'main',
    resourcePath,
    // The bare slug only when the backend resolves it to this same app; otherwise binary
    // resource URLs built from this prefix would 404 or open another manual.
    baseNavPath: hasCanonicalSlug(application)
      ? `/${params.appNameSlug}`
      : buildLegacyNavPath(application.id, application.slug, 'main'),
  };
}
